/**
 * useCapacityTasks — OutpostForge frontend (CES standard)
 * Capacity task list with encapsulated polling (the Outposts API has no
 * push events) and cancellation.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  capacityTaskRepository,
  type CapacityTaskRepository,
} from '../repositories/CapacityTaskRepository';
import { describeApiError } from '../services/apiErrors';
import { useNotificationStore } from '../stores/notificationStore';
import type { CapacityTaskSummary } from '../types/outposts';

export const TASK_POLL_MS = 2_500;

/** Retention window for the monitor: tasks older than this are not shown. */
export const TASK_RETENTION_MONTHS = 3;

/**
 * Monitor view of the task list: newest first, capped to the retention
 * window so the list does not grow without bound (the Outposts API has no
 * date filter, so the cap is applied client-side). Tasks without a
 * creationDate are kept (age unknown — hiding them would mask real tasks)
 * and sort last. ISO-8601 UTC strings compare correctly as strings.
 */
export function filterAndSortTasks(
  tasks: CapacityTaskSummary[],
  now: Date = new Date(),
): CapacityTaskSummary[] {
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - TASK_RETENTION_MONTHS);
  const cutoffIso = cutoff.toISOString();
  return tasks
    .filter((task) => !task.creationDate || task.creationDate >= cutoffIso)
    .sort((a, b) => (b.creationDate ?? '').localeCompare(a.creationDate ?? ''));
}

export function useCapacityTasks(
  filters: { outpostId?: string; status?: string },
  repository: CapacityTaskRepository = capacityTaskRepository,
) {
  const [tasks, setTasks] = useState<CapacityTaskSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const notify = useNotificationStore((state) => state.notify);
  // Polling runs every few seconds: toast only on the ok → failing
  // transition, not on every failed tick.
  const wasFailing = useRef(false);
  // Same pattern for backend-side truncation (bounded pagination): one info
  // toast on the untruncated → truncated transition, not on every tick.
  const wasTruncated = useRef(false);
  // Stale-response guard: each load() takes a generation ticket; only the
  // newest generation may apply state. Prevents a slow response from a
  // previous filter (or an unmounted component) overwriting fresh data.
  const generation = useRef(0);
  // In-flight cancellations, keyed by capacityTaskId: repeated clicks on
  // Cancel are ignored until the request settles (double-click guard). The
  // ref is the synchronous source of truth (immune to stale closures on
  // rapid clicks); the state mirrors it for UI (disabled buttons).
  const cancellingRef = useRef<Set<string>>(new Set());
  const [cancellingIds, setCancellingIds] = useState<ReadonlySet<string>>(new Set());
  // Same double-click guard for per-task refreshes.
  const refreshingRef = useRef<Set<string>>(new Set());
  const [refreshingIds, setRefreshingIds] = useState<ReadonlySet<string>>(new Set());
  // Unmount guard for refreshTask side effects (toasts). List updates are
  // self-guarding: the merge by capacityTaskId no-ops if the task is gone.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(() => {
    const gen = ++generation.current;
    repository
      .listWithMeta({
        ...(filters.outpostId ? { outpostId: filters.outpostId } : {}),
        ...(filters.status ? { statuses: [filters.status] } : {}),
      })
      .then((result) => {
        if (gen !== generation.current) return; // stale response: discard
        setTasks(filterAndSortTasks(result.tasks));
        setLoading(false);
        setError(null);
        wasFailing.current = false;
        const truncated = result.truncated === true || result.nextToken !== undefined;
        if (truncated && !wasTruncated.current) {
          notify('info', 'Task list truncated: showing the first pages of results only');
        }
        wasTruncated.current = truncated;
      })
      .catch((err: unknown) => {
        if (gen !== generation.current) return; // stale response: discard
        const message = describeApiError(err);
        setError(message);
        setLoading(false);
        if (!wasFailing.current) {
          wasFailing.current = true;
          notify('error', `Task monitor updates stopped: ${message}`);
        }
      });
  }, [filters.outpostId, filters.status, repository, notify]);

  useEffect(() => {
    setLoading(true);
    load();
    const interval = setInterval(load, TASK_POLL_MS);
    return () => {
      clearInterval(interval);
      // Invalidate in-flight responses on unmount or filter change. The
      // lint rule guards against stale DOM-node refs in cleanups; this ref
      // is a monotonic counter and bumping the LIVE value is the intent.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      generation.current++;
    };
  }, [load]);

  const cancel = useCallback(
    (task: CapacityTaskSummary) => {
      if (cancellingRef.current.has(task.capacityTaskId)) return;
      cancellingRef.current.add(task.capacityTaskId);
      setCancellingIds(new Set(cancellingRef.current));
      setCancelError(null);
      repository
        .cancel(task.outpostId, task.capacityTaskId)
        .then(() => {
          notify('success', `Capacity task ${task.capacityTaskId} cancelled`);
          load();
        })
        .catch((err: unknown) => setCancelError(describeApiError(err)))
        .finally(() => {
          cancellingRef.current.delete(task.capacityTaskId);
          setCancellingIds(new Set(cancellingRef.current));
        });
    },
    [repository, load, notify],
  );

  /**
   * Refresh a single task via GetCapacityTask and merge the fresh fields
   * into the list in place — no full-list reload. The merge is keyed by
   * capacityTaskId, so a response landing after a filter change simply
   * no-ops when the task is no longer listed.
   */
  const refreshTask = useCallback(
    (task: CapacityTaskSummary) => {
      if (refreshingRef.current.has(task.capacityTaskId)) return;
      refreshingRef.current.add(task.capacityTaskId);
      setRefreshingIds(new Set(refreshingRef.current));
      repository
        .get(task.outpostId, task.capacityTaskId)
        .then((result) => {
          setTasks((prev) =>
            prev.map((item) =>
              item.capacityTaskId === result.capacityTaskId
                ? {
                    ...item,
                    status: result.status,
                    ...(result.lastModifiedDate
                      ? { lastModifiedDate: result.lastModifiedDate }
                      : {}),
                    ...(result.completionDate ? { completionDate: result.completionDate } : {}),
                  }
                : item,
            ),
          );
        })
        .catch((err: unknown) => {
          if (mounted.current) {
            notify('error', `Could not refresh ${task.capacityTaskId}: ${describeApiError(err)}`);
          }
        })
        .finally(() => {
          refreshingRef.current.delete(task.capacityTaskId);
          setRefreshingIds(new Set(refreshingRef.current));
        });
    },
    [repository, notify],
  );

  return {
    tasks,
    loading,
    error,
    cancel,
    cancelError,
    cancellingIds,
    reload: load,
    refreshTask,
    refreshingIds,
  };
}
